import LegalDocShell from "@/components/legal/LegalDocShell";
import { buildLegalSections } from "@/lib/legalRenderer";

const SECTIONS = buildLegalSections([
  { id: "what-is-ugc", title: "1. What Is User-Generated Content?", body:
`"User-Generated Content" or "User Content" means content submitted, uploaded, posted, published, transmitted, recorded, or otherwise provided by users through NaturePulse or Farmers Pulse.

User Content may include:
- Photographs
- Videos
- Audio
- Articles
- Blog posts
- Comments
- Questions
- Answers
- Reviews
- Nature observations
- Agricultural observations
- Farm observations
- Species identifications
- Crop information
- Weather observations
- Environmental observations
- Research observations
- Farm diary entries
- Field notes
- Dates and times
- Geographic information
- GPS coordinates
- Tags
- Titles
- Descriptions
- Measurements
- Links
- Documents
- Other contributed information`
  },
  { id: "you-own", title: "2. You Own Your Original Content", body:
`NaturePulse believes contributors should retain ownership of the original material they create.

If you take a photograph and upload it to NaturePulse, you continue to own your photograph, subject to any rights you may have previously granted to others. If you write an original article, observation, description, or other creative work, you continue to own the intellectual property rights you hold in that material.

Uploading content to NaturePulse does not automatically transfer ownership to Social Pulse Inc. However, NaturePulse requires a license to use submitted content in order to operate the platform.`
  },
  { id: "license", title: "3. License You Grant NaturePulse", body:
`When you submit User Content to NaturePulse, you grant NaturePulse.net, Social Pulse Inc., and their service providers acting on their behalf a worldwide, non-exclusive, royalty-free license to host, store, reproduce, process, organize, format, resize, translate, analyze, index, display, publish, distribute, and otherwise use that content as reasonably necessary to operate and improve NaturePulse and its related services.

This may include using your content to:
- Display your observation
- Create thumbnails
- Resize photographs
- Generate maps
- Create geographic pages
- Create species pages
- Create crop pages
- Create timelines
- Generate search results
- Categorize observations
- Create tags
- Generate summaries
- Translate descriptions
- Analyze photographs
- Provide AI-assisted identification
- Connect related observations
- Generate aggregated statistics
- Create educational resources
- Support citizen science
- Support approved research uses
- Preserve historical observations
- Improve NaturePulse services
- Promote public NaturePulse content and the NaturePulse community

This license does not mean that NaturePulse owns your original content.`
  },
  { id: "public-private", title: "4. Public and Private Content", body:
`NaturePulse may provide different visibility settings. Depending upon the feature, these may include:

## Private
Visible primarily to you and authorized systems or personnel as necessary to provide the Service.

## Shared
Visible to selected users, groups, collaborators, or communities.

## Community
Visible to registered NaturePulse community members or a defined community.

## Public
Potentially visible to anyone on the Internet.

Users are responsible for selecting appropriate privacy settings where those settings are available.`
  },
  { id: "public-content", title: "5. Public Content", body:
`When you intentionally designate content as public, you understand that it may:
- Be viewed by anyone
- Appear on NaturePulse pages
- Appear on Farmers Pulse
- Appear on maps
- Appear on geographic pages
- Appear on species or crop pages
- Appear in search results
- Be indexed by Internet search engines
- Be shared through social media
- Be linked from other websites
- Be quoted or referenced by other users where legally permitted
- Contribute to aggregated NaturePulse datasets

Do not make content public if it contains information you wish to keep confidential.`
  },
  { id: "observations", title: "6. Nature Observations", body:
`NaturePulse encourages users to document observations involving plants, flowers, trees, forests, birds, animals, insects, pollinators, reptiles, amphibians, fish, marine life, habitats, rivers, lakes, wetlands, landscapes, weather, climate, environmental conditions, seasonal changes, and other aspects of nature.

Whenever possible, users should submit genuine observations with accurate dates, locations, descriptions, and photographs.`
  },
  { id: "farmers-pulse", title: "7. Farmers Pulse Contributions", body:
`Farmers Pulse allows users to contribute agricultural information. This may include:
- Crop photographs
- Crop varieties
- Planting dates
- Flowering dates
- Harvest dates
- Farm observations
- Soil conditions
- Rainfall
- Irrigation observations
- Water conditions
- Pest observations
- Plant disease observations
- Weed observations
- Pollinator observations
- Livestock observations
- Poultry observations
- Farm techniques
- Harvest information
- Yield observations
- Agricultural prices
- Agricultural questions
- Farming knowledge
- Other agricultural information

Farmers should carefully consider whether commercially sensitive information should be made public.`
  },
  { id: "farm-privacy", title: "8. Farm and Field Privacy", body:
`Agricultural information can sometimes reveal sensitive information about a farm. Before publishing an observation, consider whether it reveals:
- Exact field locations
- Valuable crops
- Equipment locations
- Livestock locations
- Water sources
- Farm buildings
- Storage facilities
- Security systems
- Private residences
- Production levels
- Commercially sensitive practices

Where NaturePulse provides location-privacy settings, farmers are encouraged to use them appropriately.`
  },
  { id: "geographic", title: "9. Geographic Information", body:
`NaturePulse observations may include geographic information such as country, state/province, district/county, city, town, village, neighborhood, general area, farm, field, or GPS coordinates.

NaturePulse may use this information to organize observations geographically. For public observations, NaturePulse may reduce the precision of geographic information where appropriate.`
  },
  { id: "sensitive-wildlife", title: "10. Sensitive Wildlife Locations", body:
`NaturePulse reserves the right to obscure, generalize, delay, restrict, or remove geographic information associated with sensitive species. Examples may include:
- Endangered species
- Threatened species
- Rare plants
- Nesting sites
- Breeding locations
- Valuable orchids
- Species vulnerable to poaching
- Sensitive habitats

Protection of wildlife may take priority over displaying exact observation coordinates.`
  },
  { id: "photographs", title: "11. Photographs", body:
`You may upload photographs that you own or have sufficient rights to use. By uploading a photograph, you represent that you have the right to submit it.

Photographs may be processed by NaturePulse for functions including:
- Resizing
- Thumbnail creation
- Image optimization
- AI analysis
- Species identification
- Crop identification
- Pest identification
- Tagging
- Categorization
- Metadata extraction
- Mapping
- Search
- Content moderation`
  },
  { id: "metadata", title: "12. Photo Metadata", body:
`Photographs may contain metadata such as date, time, device information, camera information, and GPS coordinates.

NaturePulse may process this information to assist with organizing and documenting observations. Where appropriate, certain metadata may be removed from publicly distributed copies while retained internally for authorized platform functionality.`
  },
  { id: "people", title: "13. People Appearing in Photographs", body:
`Before uploading photographs containing identifiable people, consider whether you have appropriate permission to make the photograph public. Exercise particular care with:
- Children
- Private individuals
- People on private property
- Medical situations
- Emergency situations
- Individuals in sensitive circumstances

NaturePulse may remove or restrict photographs that create significant privacy or safety concerns.`
  },
  { id: "ai-images", title: "14. AI-Generated Images", body:
`NaturePulse's scientific and historical value depends upon distinguishing actual observations from synthetic material. Do not represent an AI-generated image as a photograph of an actual observation.

AI-generated or synthetic images may be permitted for educational, illustrative, or editorial purposes when clearly labeled. For example: "AI-generated illustration — not an actual NaturePulse observation."

NaturePulse may label or remove synthetic media that could reasonably mislead users.`
  },
  { id: "edited", title: "15. Edited Photographs", body:
`Normal photographic adjustments may be acceptable, including reasonable:
- Cropping
- Exposure adjustment
- Contrast adjustment
- Sharpness adjustment
- Noise reduction
- Color correction

Do not materially alter an observation photograph in a way that creates a false record. For example, do not add an animal that was not present, remove evidence relevant to an observation, add insects, add flowers or fruit, alter crop damage, change weather conditions, create artificial flooding, or alter a species' identifying characteristics.

If substantial editing has occurred, disclose it.`
  },
  { id: "accuracy", title: "16. Accuracy of Observations", body:
`NaturePulse understands that users will sometimes make mistakes. Good-faith errors are acceptable. Intentional fabrication is not.

Do not knowingly provide false information concerning:
- Species
- Location
- Date
- Weather
- Crop conditions
- Pest outbreaks
- Plant diseases
- Agricultural prices
- Environmental events
- Wildlife sightings
- Research observations

NaturePulse may correct, label, restrict, or remove deliberately misleading content.`
  },
  { id: "ai-assisted", title: "17. AI-Assisted Content", body:
`NaturePulse may use AI to suggest:
- Titles
- Descriptions
- Identifications
- Tags
- Categories
- Translations
- Summaries
- Possible diseases
- Possible pests
- Related observations

AI-generated information can be incorrect. Users should review AI-generated material before publishing it. Where appropriate, NaturePulse may identify information as AI-generated or AI-assisted.`
  },
  { id: "third-party", title: "18. Third-Party Content", body:
`Do not upload content belonging to another person unless you have the legal right to do so. This includes:
- Photographs
- Videos
- Articles
- Illustrations
- Maps
- Research papers
- Charts
- News stories
- Social media posts
- Books
- Music
- Databases

Providing attribution does not automatically give you permission to reproduce copyrighted content.`
  },
  { id: "linking", title: "19. Linking Is Different From Copying", body:
`NaturePulse encourages users to link to useful external information. For example, users may link to research papers, government resources, news articles, educational videos, agricultural information, university publications, or conservation resources.

Whenever practical, provide a brief description and link users to the original publisher rather than copying the entire work into NaturePulse.`
  },
  { id: "source", title: "20. Source Attribution", body:
`When sharing information obtained from another source, identify the source whenever appropriate. Useful attribution may include:
- Author
- Organization
- Publication
- Website
- Original link
- Publication date

NaturePulse encourages responsible sourcing.`
  },
  { id: "import", title: "21. Content Imported From Social Media", body:
`NaturePulse may provide tools that allow users to submit links from platforms such as social networks, video services, blogs, news sites, or other websites.

NaturePulse may use the submitted link and information lawfully available from the source to create a summary, description, preview, commentary, or reference. The original source should be clearly identified and linked where appropriate.

NaturePulse should not be used to reproduce entire copyrighted works without authorization.`
  },
  { id: "comments", title: "22. Comments and Discussions", body:
`Comments, questions, answers, corrections, and discussions are also User Content. Users are responsible for what they post.

Discussions must comply with the NaturePulse Community Guidelines. NaturePulse may moderate discussions to maintain a respectful and useful community.`
  },
  { id: "personal-info", title: "23. Personal Information", body:
`Do not upload another person's sensitive personal information without authorization. Examples include:
- Home address
- Personal telephone number
- Private email address
- Financial information
- Identification documents
- Passwords
- Medical information
- Precise private location

NaturePulse may remove personal information where necessary to protect privacy or safety.`
  },
  { id: "children", title: "24. Content Involving Children", body:
`Users should exercise particular care when uploading content involving minors. Do not unnecessarily disclose:
- A child's full identity
- Home location
- School schedule
- Contact information
- Precise daily location
- Other information that could create privacy or safety risks

Appropriate parental, guardian, school, or other authorization should be obtained where required.`
  },
  { id: "research", title: "25. Research Use", body:
`NaturePulse observations may have scientific, environmental, agricultural, historical, and educational value. NaturePulse may use appropriate User Content and observation data to:
- Generate aggregated statistics
- Study environmental patterns
- Analyze agricultural patterns
- Support citizen-science initiatives
- Create anonymized or de-identified datasets
- Support educational projects
- Collaborate with researchers
- Support conservation
- Support public-interest research

Research uses involving identifiable personal information will be handled according to applicable privacy requirements and the NaturePulse Privacy Policy.`
  },
  { id: "attribution", title: "26. Attribution", body:
`Where practical, NaturePulse may attribute public contributions to the contributor's chosen display name. For example:
- Photo: Thomas V. / NaturePulse Contributor
- Observation contributed through NaturePulse

Users may be provided additional attribution preferences where available.`
  },
  { id: "removal", title: "27. Removal of Your Content", body:
`Where supported, users may delete or request removal of their User Content. Removal from the public NaturePulse interface may not immediately remove every copy from:
- Backups
- Search-engine caches
- Previously shared links
- Aggregated datasets
- Completed research
- Security records
- Legally required records

Where appropriate, NaturePulse may preserve an anonymized environmental observation after removing identifying account information.`
  },
  { id: "np-removal", title: "28. Content Removal by NaturePulse", body:
`NaturePulse may remove, restrict, label, or reduce the visibility of User Content that:
- Violates these policies
- Violates applicable law
- Infringes intellectual property rights
- Threatens privacy
- Endangers wildlife
- Contains spam
- Contains fraudulent observations
- Contains harmful misinformation
- Creates significant safety risks
- Misrepresents synthetic content as real
- Contains malicious software
- Otherwise threatens the integrity of NaturePulse`
  },
  { id: "reporting", title: "29. Reporting User Content", body:
`Users may report content for reasons including:
- Copyright violation
- Privacy violation
- False observation
- Incorrect identification
- Spam
- Harassment
- Dangerous agricultural information
- Sensitive wildlife location
- Illegal activity
- Misleading AI-generated content
- Other policy violations

NaturePulse may review reports and take appropriate action.`
  },
  { id: "copyright", title: "30. Copyright Complaints", body:
`If you believe content on NaturePulse infringes your copyright, you may submit a copyright complaint. Please provide:
- Identification of the copyrighted work
- Identification of the allegedly infringing content
- The NaturePulse page where it appears
- Your contact information
- A description of your rights in the material
- Any additional information legally required

Copyright complaints may be sent to: legal@naturepulse.net

NaturePulse may establish a separate Copyright and DMCA Policy containing additional procedures.`
  },
  { id: "commercial-use", title: "31. Commercial Use by Other Users", body:
`Unless a contributor has expressly licensed content for commercial reuse, another NaturePulse user should not assume that publicly visible photographs or other creative works are free for commercial use.

Public visibility does not automatically mean public-domain status. Anyone seeking to commercially license a user's photograph or other creative content should obtain appropriate permission from the rights holder.`
  },
  { id: "open-licensing", title: "32. Optional Open Licensing", body:
`NaturePulse may eventually allow contributors to select an optional open-content license for particular contributions. For example, users may choose to permit broader:
- Educational use
- Research use
- Noncommercial reuse
- Commercial licensing
- Attribution-based reuse

Such licensing should be optional and clearly explained. If no additional license is selected, the contributor retains their normal copyright subject to the rights granted to NaturePulse under the Terms of Service and this Policy.`
  },
  { id: "preservation", title: "33. NaturePulse Does Not Guarantee Preservation", body:
`Although NaturePulse seeks to preserve valuable environmental observations, users should not use NaturePulse as their only backup system.

We cannot guarantee permanent storage of every photograph, video, farm record, observation, article, comment, or document.

Users should maintain their own backups of important original content.`
  },
  { id: "reorganized", title: "34. Content May Be Reorganized", body:
`NaturePulse may reorganize User Content without changing its underlying meaning. For example, an observation originally posted to "Plants" might later also appear under "Wildflowers → Oregon → August Observations."

A Farmers Pulse observation might appear under "Tomatoes → Pests → Oregon → August 2026."

This organization helps users discover relevant information.`
  },
  { id: "cross-platform", title: "35. Cross-Platform NaturePulse Use", body:
`NaturePulse may operate as part of a broader ecosystem of services provided by Social Pulse Inc. Where permitted by the user's privacy settings, applicable policies, and law, public NaturePulse content may be displayed or referenced through other appropriate Social Pulse services.

Private content will not be intentionally converted into public content merely because another Social Pulse service exists. Any cross-platform use of personal information will remain subject to applicable privacy requirements.`
  },
  { id: "verification", title: "36. Community Verification", body:
`Other users may:
- Suggest identifications
- Correct classifications
- Add contextual information
- Verify observations
- Question an identification
- Provide supporting references

The original contribution and subsequent community interpretation may both become part of the observation history. NaturePulse may preserve correction histories where useful for transparency and research integrity.`
  },
  { id: "expert", title: "37. Expert Verification", body:
`NaturePulse may permit qualified contributors to provide expert verification. Experts may include:
- Botanists
- Biologists
- Ecologists
- Agricultural specialists
- Agronomists
- Entomologists
- Ornithologists
- Veterinarians
- Researchers
- Extension specialists
- Other qualified professionals

An expert verification represents the contributor's assessment and is not a guarantee by NaturePulse.`
  },
  { id: "historical", title: "38. Historical Value of Contributions", body:
`NaturePulse is intended to create a long-term record of environmental and agricultural observations. A photograph that seems ordinary today may become historically significant decades later. For example:
- When did this species first appear in this region?
- When did flowering usually occur twenty years ago?
- How has a shoreline changed?
- When did farmers begin harvesting this crop?
- How has rainfall changed?

For this reason, NaturePulse may encourage contributors to preserve accurate dates, locations, photographs, and descriptions.`
  },
  { id: "enforcement", title: "39. Enforcement", body:
`Violations of this Policy may result in:
- Content warnings
- Content labeling
- Location obscuring
- Reduced visibility
- Content removal
- Temporary posting restrictions
- Account suspension
- Account termination

NaturePulse will consider factors such as severity, intent, repetition, community safety, and the integrity of environmental records.`
  },
  { id: "appeals", title: "40. Appeals", body:
`Where available, users may request review of certain moderation or removal decisions. NaturePulse will evaluate appeals based on available information and applicable policies.`
  },
  { id: "changes", title: "41. Changes to This Policy", body:
`NaturePulse may update this User-Generated Content Policy as the platform evolves. Changes may be necessary as we introduce:
- New observation tools
- AI capabilities
- Research programs
- Farmers Pulse features
- Mapping technologies
- Community features
- Licensing options
- New Social Pulse integrations

Material changes may be communicated through NaturePulse.net or another reasonable method.`
  },
  { id: "contact", title: "42. Contact Us", body:
`Questions regarding User Content may be directed to:

NaturePulse.net, a division of Social Pulse Inc.
Website: [NaturePulse.net](https://naturepulse.net)
General Support: support@naturepulse.net
Privacy: legal@naturepulse.net
Copyright/DMCA: legal@naturepulse.net

Mailing Address:
Social Pulse Inc.
15166 SE Aston Loop
Portland, OR 97236
United States`
  },
]);

export default function UserContentPolicy() {
  return (
    <LegalDocShell
      eyebrow="NaturePulse · User-Generated Content"
      title="User-Generated Content Policy"
      effective="August 23, 2026"
      updated="August 23, 2026"
      intro={
        <>
          <p>NaturePulse.net, a division of Social Pulse Inc. ("NaturePulse," "we," "us," or "our") enables users around the world to contribute photographs, observations, videos, articles, comments, agricultural information, environmental information, and other content.</p>
          <p>These contributions help NaturePulse document the changing conditions of nature, agriculture, biodiversity, climate, wildlife, habitats, and our environment.</p>
          <p>If there is a conflict between this Policy and the <a href="/legal/terms">Terms of Service</a>, the Terms of Service will control unless otherwise required by law.</p>
        </>
      }
      sections={SECTIONS}
    />
  );
}
